import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Message } from "primereact/message";
import { TabPanel, TabView } from "primereact/tabview";

import {
  faBook,
  faBookSection,
  faBrain,
  faChartMixedUpCircleCurrency,
  faPaintBrush,
  faSearch,
  faThLarge,
  faUserShield,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useStore } from "../../../stores/StoreContext";

import "./AdminPage.scss";

import { AppRole } from "../../../../@api";

import { applicationConfig } from "../../../../environmentConfiguration";
import { translationKeys } from "../../../models/app-translation-keys";
import { CostOverviewTab } from "./CostOverviewTab/CostOverviewTab";
import { DisclaimersTab } from "./DisclaimersTab/DisclaimersTab";
import { FeatureTilesTab } from "./FeatureTilesTab/FeatureTilesTab";
import { GlossaryTab } from "./GlossaryTab/GlossaryTab";
import PermissionsTab from "./PermissionsTab/PermissionsTab";
import { SystemPromptsTab } from "./SystemPromptsTab/SystemPromptsTab";
import TermsOfUseUploadTab from "./TermsOfUseTab/TermsOfUseTab";
import { WhiteLabelTab } from "./WhiteLabelTab/WhiteLabelTab";

enum AdminAreaTabKeys {
  WhiteLabel = "whiteLabel",
  CMS = "cms",
  Users = "users",
  AppRoles = "appRoles",
  TermsOfUse = "termsOfUse",
  Glossary = "glossary",
  Disclaimers = "disclaimers",
  FeatureTiles = "featureTiles",
  SystemPrompts = "systemPrompts",
}

interface IAdminAreaTab {
  key: string;
  headerKey: string;
  icon: IconDefinition;
  component: ReactNode;
  permissions: AppRole[] | (() => boolean);
  isHidden?: boolean;
}

const AdminPageRaw: FunctionComponent = () => {
  const { permissionStore } = useStore();
  const { t } = useTranslation();

  const [hasAnyPermissions, setHasAnyPermissions] = useState(true);

  const adminAreaTabs: IAdminAreaTab[] = [
    {
      key: AdminAreaTabKeys.WhiteLabel,
      headerKey: translationKeys.adminArea.whiteLabel.tabLabel,
      icon: faPaintBrush,
      component: <WhiteLabelTab />,
      permissions: [AppRole.AppCustomizationAdmin],
      isHidden: !applicationConfig.featureFlags.enableWhiteLabel,
    },
    {
      key: AdminAreaTabKeys.Users,
      headerKey: translationKeys.adminArea.costOverview.tabLabel,
      icon: faChartMixedUpCircleCurrency,
      component: <CostOverviewTab />,
      permissions: [AppRole.UserManagementAdmin],
    },
    {
      key: AdminAreaTabKeys.AppRoles,
      headerKey: translationKeys.adminArea.appRoles.tabLabel,
      icon: faUserShield,
      component: <PermissionsTab />,
      permissions: () => permissionStore.isPermissionAdmin(),
    },
    {
      key: AdminAreaTabKeys.Glossary,
      headerKey: translationKeys.adminArea.glossary.tabLabel,
      icon: faBook,
      component: <GlossaryTab />,
      permissions: [AppRole.GlossaryAdmin],
    },
    {
      key: AdminAreaTabKeys.TermsOfUse,
      headerKey: translationKeys.adminArea.termsOfUse.tab.label,
      icon: faBookSection,
      component: <TermsOfUseUploadTab />,
      permissions: [AppRole.LegalInformationAdmin],
      /* Feature Flag for Terms of Use is used here */
      isHidden: !applicationConfig.featureFlags.enableTermsOfUse,
    },
    {
      key: AdminAreaTabKeys.Disclaimers,
      headerKey: translationKeys.adminArea.disclaimers.tabLabel,
      icon: faSearch,
      component: <DisclaimersTab />,
      permissions: [AppRole.LegalInformationAdmin],
    },
    {
      key: AdminAreaTabKeys.FeatureTiles,
      headerKey: translationKeys.adminArea.featureTiles.tabLabel,
      icon: faThLarge,
      component: <FeatureTilesTab />,
      permissions: [AppRole.TileManagementAdmin],
    },
    {
      key: AdminAreaTabKeys.SystemPrompts,
      headerKey: translationKeys.adminArea.systemPrompts.tabLabel,
      icon: faBrain,
      component: <SystemPromptsTab />,
      permissions: [AppRole.ContentManagementAdmin],
    },
  ];

  useEffect(() => {
    if (
      permissionStore.hasAnyRole([
        AppRole.LegalInformationAdmin,
        AppRole.UserManagementAdmin,
        AppRole.ContentManagementAdmin,
        AppRole.AppCustomizationAdmin,
        AppRole.GlossaryAdmin,
        AppRole.TileManagementAdmin,
      ])
    ) {
      setHasAnyPermissions(true);
    } else {
      setHasAnyPermissions(false);
    }
  }, [permissionStore.userPermissions]);

  return (
    <div className="admin-page-container">
      {hasAnyPermissions ? (
        <TabView scrollable>
          {adminAreaTabs
            .filter(
              tab =>
                !tab.isHidden &&
                (typeof tab.permissions === "function"
                  ? tab.permissions()
                  : permissionStore.hasAnyRole(tab.permissions)),
            )
            .map(tab => (
              <TabPanel
                key={tab.key}
                header={t(tab.headerKey)}
                leftIcon={<FontAwesomeIcon size="sm" icon={tab.icon} />}
              >
                {tab.component}
              </TabPanel>
            ))}
        </TabView>
      ) : (
        <Message severity="error" className="permission-error" text={t(translationKeys.adminArea.errorNoPermissions)} />
      )}
    </div>
  );
};

export const AdminPage = observer(AdminPageRaw);
